import {
  DEFAULT_HARDWARE_CONFIG,
  getDefaultInfrastructure,
  getSensor,
  getTelescopePackage,
  INFRASTRUCTURE_LINES,
} from '../data/hardwareCatalog';

export const BASELINE_COMPUTE_PER_MODULE = 75000;
export const BASELINE_MEGAPIXELS = 62;
export const BASELINE_CAMERAS_PER_MODULE = 6;

/**
 * QHY-only: scale compute from 6×62 MP @ $75k baseline by megapixels and camera count.
 * ZWO/ATIK (~62 MP) do not auto-update compute.
 */
export function computeSuggestedComputePerModule(telescopePackageId, sensorId) {
  if (sensorId !== 'qhy411') {
    return null;
  }
  const telescopePkg = getTelescopePackage(telescopePackageId);
  const sensor = getSensor(sensorId);
  return Math.round(
    BASELINE_COMPUTE_PER_MODULE *
      (sensor.megapixels / BASELINE_MEGAPIXELS) *
      (telescopePkg.count / BASELINE_CAMERAS_PER_MODULE),
  );
}

/**
 * @param {object} config
 * @param {string} config.telescopePackageId
 * @param {string} config.sensorId
 * @param {Record<string, number>} [config.infrastructure]
 */
export function computeHardwareCost(config) {
  const telescopePackageId =
    config.telescopePackageId ?? DEFAULT_HARDWARE_CONFIG.telescopePackageId;
  const sensorId = config.sensorId ?? DEFAULT_HARDWARE_CONFIG.sensorId;
  const infrastructure = {
    ...getDefaultInfrastructure(),
    ...(config.infrastructure ?? {}),
  };

  const telescopePkg = getTelescopePackage(telescopePackageId);
  const sensor = getSensor(sensorId);
  const telescopesPerModule = telescopePkg.count;

  const breakdown = [];

  breakdown.push({
    id: 'telescopes',
    name: telescopePkg.label,
    qty: telescopesPerModule,
    unitCost: telescopePkg.unitCost,
    subtotal: telescopesPerModule * telescopePkg.unitCost,
    editable: false,
  });

  breakdown.push({
    id: 'cameras',
    name: `${sensor.label} (per telescope)`,
    qty: telescopesPerModule,
    unitCost: sensor.unitCost,
    subtotal: telescopesPerModule * sensor.unitCost,
    editable: false,
  });

  INFRASTRUCTURE_LINES.forEach((line) => {
    const unitCost = infrastructure[line.id] ?? 0;
    breakdown.push({
      id: line.id,
      name: line.name,
      qty: 1,
      unitCost,
      subtotal: unitCost,
      editable: true,
    });
  });

  const hardwareCostPerModule = breakdown.reduce((sum, row) => sum + row.subtotal, 0);

  const telescopeSpecs = {
    aperture: telescopePkg.aperture,
    focalRatio: telescopePkg.focalRatio,
    sensor: sensor.label,
    sensorWidth: sensor.sensorWidth,
    sensorHeight: sensor.sensorHeight,
    pixelSize: sensor.pixelSize,
  };

  return {
    telescopesPerModule,
    telescopeSpecs,
    telescopePackageId: telescopePkg.id,
    sensorId: sensor.id,
    breakdown,
    hardwareCostPerModule,
    computeCostPerModule: computeSuggestedComputePerModule(
      telescopePkg.id,
      sensor.id,
    ),
    infrastructure,
  };
}

/**
 * @param {object} costBaseParams
 * @param {number} hardwareCost
 * @param {number} [computeCost]
 */
export function applyHardwareCostToBaseParams(
  costBaseParams,
  hardwareCost,
  computeCost,
) {
  if (!costBaseParams?.costItems) {
    return costBaseParams;
  }

  const costItems = costBaseParams.costItems.map((item) => {
    if (item.id === 'hardwareCost') {
      return { ...item, value: hardwareCost };
    }
    if (computeCost != null && item.id === 'computeCost') {
      return { ...item, value: computeCost };
    }
    return item;
  });

  return { ...costBaseParams, costItems };
}
